import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearReadAlerts,
  deleteAlert,
  fetchAlertSummary,
  fetchAlerts,
  markAlertRead,
  markAllAlertsRead,
  starAlert,
  unstarAlert,
} from '../api/alerts';
import type { AlertFilters, AlertPage, AlertSummary } from '../types';
import { alertKeys } from './queryKeys';

/** How often the bell's count is asked again while the application is open. */
const SUMMARY_POLL_MS = 60_000;

/** The caller's notifications, newest first. "Load more" follows the server's cursor. */
export function useAlertList(filters: AlertFilters) {
  return useInfiniteQuery<
    AlertPage,
    Error,
    { pages: AlertPage[] },
    readonly unknown[],
    string | null
  >({
    queryKey: alertKeys.list(filters),
    queryFn: ({ pageParam }) => fetchAlerts(filters, pageParam),
    initialPageParam: null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
  });
}

/** Total, unread, starred and the last seven days. The bell reads `unread` from it. */
export function useAlertSummary() {
  return useQuery<AlertSummary>({
    queryKey: alertKeys.summary(),
    queryFn: fetchAlertSummary,
    refetchInterval: SUMMARY_POLL_MS,
  });
}

/**
 * Everything that changes a notification. Each one reads the list and the counts again afterwards,
 * since the server decides what they now say. None swallows a failure.
 */
export function useAlertActions() {
  const queryClient = useQueryClient();
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: alertKeys.all });
  };

  return {
    markRead: useMutation({ mutationFn: markAlertRead, onSuccess: refresh }),
    markAllRead: useMutation({ mutationFn: markAllAlertsRead, onSuccess: refresh }),
    star: useMutation({ mutationFn: starAlert, onSuccess: refresh }),
    unstar: useMutation({ mutationFn: unstarAlert, onSuccess: refresh }),
    remove: useMutation({ mutationFn: deleteAlert, onSuccess: refresh }),
    clearRead: useMutation({ mutationFn: clearReadAlerts, onSuccess: refresh }),
  };
}
